import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideDropdownOpen, setGuideDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setGuideDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const guideArticles = [
    { name: 'Ultimate Guide', slug: '/basetao-spreadsheet-guide' },
    { name: 'Basetao Review', slug: '/basetao-review' },
    { name: 'Shipping Cost Guide', slug: '/basetao-shipping-cost' },
    { name: 'Coupon & Discounts', slug: '/basetao-coupon' },
    { name: 'Warehouse Guide', slug: '/basetao-warehouse' },
    { name: 'Is Basetao Legit?', slug: '/is-basetao-legit' },
    { name: 'Best Finds 2026', slug: '/basetao-finds' },
    { name: 'Alternatives Compared', slug: '/basetao-alternatives' },
    { name: 'Shipping Calculator', slug: '/basetao-shipping-calculator' },
    { name: 'Beginner Tutorial', slug: '/basetao-beginner-guide' },
    { name: 'Basetao vs Superbuy', slug: '/basetao-vs-superbuy' },
    { name: 'Basetao vs Sugargoo', slug: '/basetao-vs-sugargoo' },
    { name: 'Basetao vs Pandabuy', slug: '/basetao-vs-pandabuy' },
    { name: 'Europe Shipping', slug: '/basetao-europe-shipping' },
    { name: 'USA Shipping', slug: '/basetao-usa-shipping' },
    { name: 'QC Photos Guide', slug: '/basetao-qc-photos' },
    { name: 'Tracking Guide', slug: '/basetao-tracking-guide' },
    { name: 'Budget Finds', slug: '/budget-basetao-finds' },
    { name: 'Power User Tips', slug: '/basetao-spreadsheet-tips' },
    { name: 'Discount Code Guide', slug: '/basetao-discount-code' },
  ];

  const showTransparent = isHome && !scrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          showTransparent
            ? 'bg-transparent'
            : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </span>
            <span className={`font-heading font-bold text-lg whitespace-nowrap ${
              showTransparent ? 'text-white' : 'text-foreground-950'
            }`}>
              Basetao Spreadsheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                showTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              Home
            </Link>
            <a href="#what-is" className={`text-sm font-medium transition-colors whitespace-nowrap ${
              showTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-600'
            }`}>About</a>
            <a href="#categories" className={`text-sm font-medium transition-colors whitespace-nowrap ${
              showTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-600'
            }`}>Categories</a>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => setGuideDropdownOpen(!guideDropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  showTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                <span className="w-4 h-4 inline-flex items-center justify-center">
                  <i className="ri-guide-line text-sm"></i>
                </span>
                Shopping Guide
                <i className={`${guideDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
              </button>

              {guideDropdownOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200 py-2 max-h-80 overflow-y-auto shadow-lg">
                  {guideArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={article.slug}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                    >
                      <span className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-article-line text-sm text-primary-500"></i>
                      </span>
                      {article.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a href="#faq" className={`text-sm font-medium transition-colors whitespace-nowrap ${
              showTransparent ? 'text-white/80 hover:text-white' : 'text-foreground-700 hover:text-primary-600'
            }`}>FAQ</a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                  showTransparent
                    ? 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <span>Browse Products</span>
                <i className={`${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 py-2 max-h-80 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <span className="w-7 h-7 flex items-center justify-center">
                        <i className={`${cat.icon} text-base text-primary-500`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${
              showTransparent ? 'text-white' : 'text-foreground-800'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Slide-out Menu */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setMobileOpen(false)}
        ></div>
        <div
          className={`absolute top-0 right-0 w-80 max-w-[85vw] h-full bg-background-50 overflow-y-auto transition-transform duration-300 ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-background-200">
            <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
              <span className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </span>
              <span className="font-heading font-bold text-lg text-foreground-950 whitespace-nowrap">
                Basetao Spreadsheet
              </span>
            </Link>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          <div className="p-4 space-y-2">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-primary-50 transition-colors"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                <i className="ri-home-4-line text-primary-600"></i>
              </span>
              <span className="font-medium text-sm">Home</span>
            </Link>

            <p className="px-4 pt-4 pb-1 text-xs font-semibold text-foreground-400 uppercase tracking-wide">Navigation</p>
            <a href="#what-is" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-primary-50 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-information-line text-primary-500"></i></span>
              <span className="text-sm">About Basetao</span>
            </a>
            <a href="#categories" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-primary-50 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-grid-line text-primary-500"></i></span>
              <span className="text-sm">Categories</span>
            </a>
            <a href="#faq" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-primary-50 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-question-answer-line text-primary-500"></i></span>
              <span className="text-sm">FAQ</span>
            </a>

            <p className="px-4 pt-4 pb-1 text-xs font-semibold text-foreground-400 uppercase tracking-wide">Shopping Guides</p>
            {guideArticles.map((article) => (
              <Link
                key={article.slug}
                to={article.slug}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-primary-50 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-article-line text-primary-500"></i>
                </span>
                <span className="text-sm">{article.name}</span>
              </Link>
            ))}

            <p className="px-4 pt-4 pb-1 text-xs font-semibold text-foreground-400 uppercase tracking-wide">Product Categories</p>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-primary-50 transition-colors"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-50">
                  <i className={`${cat.icon} text-sm text-primary-500`}></i>
                </span>
                <span className="text-sm">{cat.name}</span>
              </a>
            ))}

            <div className="pt-4 pb-2 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shopping-bag-3-line"></i></span>
                Basetao Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-rocket-line"></i></span>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}